import { ListItemProps } from "@/components/atoms/list";
import { InteractionState } from "../types";
import { VoicemailInteractionsProps } from ".";
import { useMemo } from "react";
import {
	InteractionDto,
	Queue,
	InteractionHandlingStatus,
} from "@/repository/models";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchVoicemailsFn } from "@/repository/queryFns/voicemails";
import { notificationListener } from "@/hooks/notification";
import { ServerEvent } from "@/common/notification";
import { useInteractionsHandler } from "@/hooks/interactions";
import { formatDate } from "@/common/helpers/dateHelper";
import { useShadowStyle } from "@/common/theme/shadowStyles";
import { InteractionPanelItemDescription } from "../types";
import { InteractionIconOverlay } from "@/components/molecules/interactionIconOverlay";
import {
	InteractionListAccordionItem,
	InteractionListAccordionItemProps,
} from "@/components/molecules/listAccordionItem";
import { useResponsiveBreakpoints } from "@/hooks/responsive";
import { InteractionListIcon } from "@/components/molecules/interactionListIcon";
import { useQueuesStore, QueuesState } from "@/common/stores/nonpersistingStores/queues";
import { Flex } from "@/components/atoms/flex";

export const VoicemailInteractions = ({
	hideVoicemails,
	classesName,
	getHoverIcon,
	renderHeaderTags,
	renderInlineHeaderTags,
	refCallback,
	searchText,
	queueIds,
	mountNode,
}: VoicemailInteractionsProps) => {
	const queryKey = ["voicemails"];

	const shadowStyle = useShadowStyle();
	const queues = useQueuesStore((state: QueuesState) => state.queues);

	const { data: voicemails } = useQuery<InteractionDto[]>({
		queryKey: queryKey,
		queryFn: fetchVoicemailsFn,
		refetchOnMount: "always",
	});
	const queryClient = useQueryClient();

	const interactionHandler = useInteractionsHandler();

	const { isMobile } = useResponsiveBreakpoints();

	const getFilteredVoicemails = (interactions: InteractionDto[]) => {
		return searchText.length > 0 || queueIds.length > 0
			? interactions.filter((i: InteractionDto) => {
					const lowerSearchText = searchText?.toLowerCase();
					const attributesArray = [
						i.name?.toLowerCase() || "",
						i.contactInformation?.toLowerCase() || "",
						i.description?.toLowerCase() || "",
					];

					const isSearchTextFiltered =
						searchText.length > 0
							? attributesArray.some((x) => x.startsWith(lowerSearchText))
							: true;

					const isQueueIdFiltered =
						queueIds.length > 0 ? queueIds.includes(i.queueId) : true;

					return isSearchTextFiltered && isQueueIdFiltered;
				})
			: interactions;
	};

	const queuesWithSkillsBasedRoutingEnabled = useMemo(() => {
		return new Set(
			queues.filter((x) => x.isSkillBasedRoutingEnabled).map((x) => x.id)
		);
	}, [queues]);

	const voicemailsPanelView = useMemo<ListItemProps[]>(
		() =>
			voicemails && !hideVoicemails
				? getFilteredVoicemails(voicemails).map((i: InteractionDto) => {
						return {
							key: i.id,
							header: i.name || i.contactInformation,
							headerAdornment: renderInlineHeaderTags(i),
							subHeader: i.contactInformation,
							content: i.description,
							media: isMobile ? (
								<InteractionListIcon
									interaction={i}
									interactionHandlingStatus={InteractionHandlingStatus.Waiting}
								/>
							) : (
								<InteractionIconOverlay
									interaction={i}
									itemPanelStatus={InteractionPanelItemDescription.Waiting}
									interactionHandlingStatus={InteractionHandlingStatus.Waiting}
									mountNode={mountNode}
									isSkillBasedRoutingEnabled={queuesWithSkillsBasedRoutingEnabled.has(
										i.queueId
									)}
								/>
							),
							headerMedia: (
								<>
									<Flex column className={classesName?.listHeaderQueueColumn}>
										<div className={"additionalContent"} title={i.queueName}>
											{i.queueName}
										</div>
										{renderHeaderTags(i)}
									</Flex>
									<span>{formatDate(new Date(i.startDate), false, true)}</span>
								</>
							),
							endMedia: getHoverIcon(i),
						};
					})
				: [],
		[
			voicemails,
			hideVoicemails,
			getHoverIcon,
			searchText,
			queueIds,
			classesName?.listHeaderQueueColumn,
			renderHeaderTags,
			renderInlineHeaderTags,
		]
	);

	notificationListener<InteractionDto>(
		ServerEvent.WaitingVoicemailChanged,
		(interaction: InteractionDto) => {
			const updatedWaiting = interactionHandler.processInteractionsChange(
				interaction,
				voicemails
			);

			queryClient.setQueryData<InteractionDto[]>(queryKey, updatedWaiting);
		}
	);

	notificationListener(ServerEvent.UserMembershipChanged, (_: Queue) => {
		queryClient.invalidateQueries({ queryKey: queryKey });
	});

	notificationListener(ServerEvent.UserMembershipRemoved, (_: Queue) => {
		queryClient.invalidateQueries({ queryKey: queryKey });
	});

	const accordionConfig: InteractionListAccordionItemProps = useMemo(
		() => ({
			value: InteractionState.Voicemail,
			items: voicemailsPanelView,
			listClassNames: {
				endMedia: classesName?.interactionTimer,
				shadow: shadowStyle.right,
				headerMedia: classesName?.listHeaderMedia,
				startMedia: classesName?.startMedia,
				startMediaSelected: classesName?.startMedia,
			},
			ref: refCallback,
			unfilteredNumberOfItems: voicemails?.length ?? 0,
		}),
		[voicemailsPanelView, classesName]
	);

	return <InteractionListAccordionItem {...accordionConfig} />;
};
